
import { Capacitor } from "@capacitor/core";
import { DefaultClientAuthenticationService } from "@cervval.js/client-auth/src/default.clientAuthentication.service";
import { ClientRouteOptions, CredentialsPolicy } from "@cervval.js/client-routes";
import { TypedClientAjaxProxy } from "@cervval.js/client-routes/src/typedClientAjaxProxy.decorator";
import { XhrClientRoutesService } from "@cervval.js/client-routes/src/xhr.clientRoutes.service";
import { ClientWebSocketService, Rooms } from "@cervval.js/client-websocket/src/clientWebSocket.service";
import { LoginData } from "@cervval.js/common-auth/src/loginData.model";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { anyReallyAny } from "@cervval.js/common/tools";
import { WebSocketRefreshService, WS_REFRESH_SERVICE } from "@ehealth_tools/client-security/src/services/websocketRefresh.service";
import { CapacitorConfiguration, PROTOCOL } from "@ehealth_tools/common-capacitor/src/definitions/capacitorConfig.definition";
import { BaseElementTypes } from "@ehealth_tools/common/src/data/dto/base.dto";
import { SimpleLoginRoutes } from "@ehealth_tools/simple-login-common/src/simpleLogin.routes";
import { UsersRoutes } from "@ehealth_tools/simple-login-common/src/users.routes";

export const CAPACITOR_SERVICE = "CAPACITOR_SERVICE";
/** 
 * This service help to mitigate the app to run on both mobile (native platform) or browser
 * If your app is intended to run on native platform, please always use this service to interact with capacitor functionalities
 */
@Injectable(CAPACITOR_SERVICE)
export class CapacitorService<
    AppLoginData extends LoginData<anyReallyAny>,
    ElementTypes extends BaseElementTypes,
    FetchDataTypes,
    FetchMethod extends keyof FetchDataTypes = keyof FetchDataTypes
> {

    @TypedClientAjaxProxy()
    public usersRoutes!: UsersRoutes<ClientRouteOptions, AppLoginData>;

    @TypedClientAjaxProxy()
    private _loginRoutes!: SimpleLoginRoutes<ClientRouteOptions>;

    /** Is the app running in android or iOS ? */
    private _isNativeApp: boolean = false;

    /** Get if the app is running in android or iOS */
    public isNativeApp(): boolean {
        return this._isNativeApp;
    }

    /** Returns true if current application is running in an iOS environment */
    public isRunningOnIOS(): boolean {
        return Capacitor.getPlatform() === "ios";
    }

    /** Returns true if current application is running in an Android environment */
    public isRunningOnAndroid(): boolean {
        return Capacitor.getPlatform() === "android";
    }

    // Init base url with window information (for browser)
    private _config: CapacitorConfiguration = {
        hostname: window.location.hostname,
        port: window.location.port.length ? Number(window.location.port) : null,
        protocol: window.location.protocol === "https:" ? "https" : "http"
    };

    /**
     * Constructor of ```CapacitorService```
     * @param _params params object of instance (private)
     * @param _params.fetchOptions Fetch options object
     * @param _params.fetchOptions.method Method name for fetch routes
     * @param _params.fetchOptions.variables Variables to send on fetch
     * @param _params.routesServiceOptions Optional. `Partial<ClientRouteOptions>` to override default options of ClientRoutesService. Be aware though that the `urlPrefix` options will be override by service, so that urlPrefix will be added to current server URL (found in the configuration of the service)
     */
    constructor(protected _params: {
        fetchOptions?: {
            method: FetchMethod,
            variables: FetchDataTypes[FetchMethod]
        },
        routesServiceOptions?: Partial<ClientRouteOptions>
    }, private _softConfig?: { protocol?: PROTOCOL, hostname?: string, port?: number }) {
        this._initialize();
    }

    /** */
    private _initialize(): void {
        if (Capacitor.isNativePlatform()) {
            this._isNativeApp = true;
        }

        this._config.hostname = __capacitor_hostname__ ?? this._softConfig?.hostname ?? this._config.hostname;
        this._config.port = __capacitor_port__ ?? this._softConfig?.port ?? this._config.port;
        this._config.protocol = __capacitor_protocol__ as PROTOCOL ?? this._softConfig?.protocol ?? this._config.protocol;

        this._initNetworkServices(); // Re set the XHR urlPrefix
    }

    /** Initialize the networks (xhr, ws)  services*/
    private _initNetworkServices(): void {

        let options: ClientRouteOptions;
        if (this._params.routesServiceOptions != null) {
            options = {
                showHourGlass: false,
                withCredentials: CredentialsPolicy.ALWAYS,
                ...this._params.routesServiceOptions,
                // urlPrefix is always override by service
                urlPrefix: new URL(this._params.routesServiceOptions?.urlPrefix ?? "", this.getServerURLFromConfig()).toString() + "/"
            };
        } else {
            options = {
                showHourGlass: false,
                urlPrefix: this.getServerURLFromConfig().toString() + "/",
                withCredentials: CredentialsPolicy.ALWAYS
            };
        }

        // We must override the XHRClient before to establish any other network connection (websocket) since it is the root network access
        injector.register(new XhrClientRoutesService(options), { overrideIfDependencyRegistered: true });// ClientRoutesService
    }


    /** Initialize */
    private _initWebSocketServices(): void {
        // ClientWebSocketService must be initialize after XhrClient (it uses it for handshake)
        const cwsService = new ClientWebSocketService();
        injector.register(cwsService, { overrideIfDependencyRegistered: true });
        cwsService.init({ uri: this.getServerURLFromConfig().toString(), webSocketPath: (this._params.routesServiceOptions?.urlPrefix ?? "") + "/socket.io" });// initiate connection to server
        cwsService.registerApplicationRooms({ enterRooms: [Rooms.ALL_DATA_UPDATES] });

        // If fetchOptions are defined, we are binding web socket events with registered WebSocketRefreshService
        if (this._params.fetchOptions != null) {
            const wsrs = injector.find<WebSocketRefreshService<ElementTypes, FetchDataTypes, FetchMethod>>(WS_REFRESH_SERVICE);
            if (wsrs != null) {
                wsrs.bindWebSocketEvents(this._params.fetchOptions.method, this._params.fetchOptions.variables);
            } else {
                getLogConsole().warn("[Capacitor service]", "[_initWebSocketServices]", "Expected WebSocketRefreshService to be registered for injection");
            }
        }
    }


    /** Return the application server url as an URL object from service configuration */
    public getServerURLFromConfig(suffix?: string): URL {
        return new URL(`${this._config.protocol}://${this._config.hostname}${this._config.port != null ? ":" + this._config.port : ""}${suffix ?? ""}`.replaceAll("//", "/"));
    }

    /** */
    public async start(onAuth: (loginData: AppLoginData | null) => Promise<void>): Promise<AppLoginData | null> {
        if (await this._loginRoutes.amIConnected()) {
            const loginData = await this.usersRoutes.getLoginData();
            if (loginData != null) {
                this._initWebSocketServices();
                injector.register(new DefaultClientAuthenticationService(loginData), { overrideIfDependencyRegistered: true });
            }
            await onAuth(loginData);
            return loginData;
        } else {
            await onAuth(null);
            return null;
        }
    }

    /** Call logout route to log user out, and reload current location */
    public async logout(): Promise<void> {
        await this._loginRoutes.logout().then(() => {
            window.location.reload();
        }).catch(e => getLogConsole().error("[CapacitorService]", "logout", e));
    }

    /**
     * Return current CapacitorService registered for injection
     * @throws If CapacitorService isn't registered before navigation 
     */
    public static inject<
        AppLoginData extends LoginData<anyReallyAny>,
        ElementTypes extends BaseElementTypes,
        FetchDataTypes,
        FetchMethod extends keyof FetchDataTypes = keyof FetchDataTypes>(): CapacitorService<AppLoginData, ElementTypes, FetchDataTypes, FetchMethod> {
        return injector.get<CapacitorService<AppLoginData, ElementTypes, FetchDataTypes, FetchMethod>>(CAPACITOR_SERVICE);
    }
}